/**
 * Volume pane settings: how the Vest bars are scaled and the CME volume-spike
 * rule. Visibility toggles live in levelsStore (`vestVolume`, `cmeVolSpikes`,
 * `vestVolSpikes`); the Vest spike rule is shared with the candle 🐋
 * (vestSpikeSettingsStore). Text in the pane: `legendVisible` (the eye toggle
 * in the pane corner) and `showSpikeMultiples` (the "2.4x" labels over CME
 * spike bars; the brighter bar and the 🐋 stay either way).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { VestScaleMode } from '../utils/vestVolume';
import { clampCmeSpikeSettings, CmeSpikeSettings, DEFAULT_CME_SPIKE_SETTINGS } from '../utils/cmeVolumeSpikes';

interface VolumePaneSettingsState {
  vestScale: VestScaleMode;
  cme: CmeSpikeSettings;
  legendVisible: boolean;
  showSpikeMultiples: boolean;
  setVestScale: (m: VestScaleMode) => void;
  setCme: (s: Partial<CmeSpikeSettings>) => void;
  setLegendVisible: (v: boolean) => void;
  setShowSpikeMultiples: (v: boolean) => void;
}

type PersistedVolumePaneSettings = Pick<VolumePaneSettingsState, 'vestScale' | 'cme' | 'legendVisible' | 'showSpikeMultiples'>;

/** Persisted state from any earlier version; the text flags default to shown. */
export function mergeVolumePaneSettings(persisted: unknown): PersistedVolumePaneSettings {
  const p = (persisted && typeof persisted === 'object' ? persisted : {}) as Record<string, unknown>;
  return {
    vestScale: p.vestScale === 'same' ? 'same' : 'independent',
    cme: clampCmeSpikeSettings((p.cme && typeof p.cme === 'object' ? p.cme : {}) as Partial<CmeSpikeSettings>),
    legendVisible: p.legendVisible !== false,
    showSpikeMultiples: p.showSpikeMultiples !== false,
  };
}

export const useVolumePaneSettingsStore = create<VolumePaneSettingsState>()(
  persist(
    (set, get) => ({
      vestScale: 'independent',
      cme: { ...DEFAULT_CME_SPIKE_SETTINGS },
      legendVisible: true,
      showSpikeMultiples: true,
      setVestScale: (m) => set({ vestScale: m === 'same' ? 'same' : 'independent' }),
      setCme: (s) => set({ cme: clampCmeSpikeSettings({ ...get().cme, ...s }) }),
      setLegendVisible: (v) => set({ legendVisible: v }),
      setShowSpikeMultiples: (v) => set({ showSpikeMultiples: v }),
    }),
    {
      name: 'volume-pane-settings',
      partialize: (s) => ({ vestScale: s.vestScale, cme: s.cme, legendVisible: s.legendVisible, showSpikeMultiples: s.showSpikeMultiples }),
      merge: (persisted, current) => ({ ...current, ...mergeVolumePaneSettings(persisted) }),
    },
  ),
);
